import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    Plus,
    Search,
    Trash2,
    Edit,
    X,
    Check,
    Loader2,
    FileText,
    Users,
    Server,
    RefreshCw,
    Layers,
    Cpu,
    Copy
} from 'lucide-react'
import { templateApi, avatarApi, dialerApi, linksApi, voiceSetApi } from '../services/api'
import SearchableDropdown from '../components/SearchableDropdown'
import './Templates.css'

function Templates() {
    // Data states
    const [templates, setTemplates] = useState([])
    const [avatars, setAvatars] = useState([])
    const [dialers, setDialers] = useState([])
    const [accessLinks, setAccessLinks] = useState([])
    const [voiceSets, setVoiceSets] = useState([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)

    // Filter
    const [searchQuery, setSearchQuery] = useState('')

    // Modal states
    const [showModal, setShowModal] = useState(false)
    const [modalMode, setModalMode] = useState('add')
    const [currentTemplate, setCurrentTemplate] = useState(null)
    const [saving, setSaving] = useState(false)

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        sip_pool: '',
        password: '',
        password_same_as_sip: false,
        dialer_id: '',
        avatar_id: '',
        access_link: '',
        campaign: '',
        campaign_name: '',
        // Bot configuration
        login_method: 'double-merged',
        transfer_method: 'd1',
        xfer_disposition_selector: 'call-transfered',
        transcriber_type: 'VOSK',
        dont_wait: true,
        comment_login_submit_btn: true,
        comment_2nd_hello_logic: true,
        cmnt_40sec_call_limit_check: false,
        dispo_in_cb_after_40sec: false,
        debug_mode: false,
        number_of_audios: 1,
        save_logs_for_sips: '',
        voice_set_pool: '',
        voice_set_ids: []
    })

    // Show advanced config toggle
    const [showAdvanced, setShowAdvanced] = useState(false)

    // SIP pool preview
    const [sipPreview, setSipPreview] = useState({ valid: false, count: 0, sips: [] })

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [templateToDelete, setTemplateToDelete] = useState(null)

    useEffect(() => {
        loadData()
    }, [])

    useEffect(() => {
        parseSipPool(formData.sip_pool)
    }, [formData.sip_pool])

    const loadData = async () => {
        try {
            const [templatesRes, avatarsRes, dialersRes, linksRes, voiceSetsRes] = await Promise.all([
                templateApi.getAll(),
                avatarApi.getAll(),
                dialerApi.getAll(),
                linksApi.getAll(),
                voiceSetApi.getAll()
            ])

            setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : [])
            setAvatars(Array.isArray(avatarsRes.data) ? avatarsRes.data.filter(a => !a.archived) : [])
            setDialers(Array.isArray(dialersRes.data) ? dialersRes.data.filter(d => !d.archived) : [])
            setVoiceSets(Array.isArray(voiceSetsRes.data) ? voiceSetsRes.data : [])

            if (Array.isArray(linksRes.data) && linksRes.data.length > 0) {
                try {
                    const links = JSON.parse(linksRes.data[0].links || '[]')
                    setAccessLinks(links)
                } catch (e) {
                    setAccessLinks([])
                }
            }
        } catch (error) {
            console.error('Failed to load data:', error)
        } finally {
            setLoading(false)
        }
    }

    const refresh = async () => {
        setRefreshing(true)
        await loadData()
        setRefreshing(false)
    }

    const parseSipPool = (sipPoolString) => {
        if (!sipPoolString || sipPoolString.trim() === '') {
            setSipPreview({ valid: false, count: 0, sips: [] })
            return
        }

        try {
            const sips = []
            const parts = sipPoolString.split(',').map(p => p.trim()).filter(p => p)

            for (const part of parts) {
                if (part.includes('-')) {
                    // Could be range: "8001-8005" or "Agent1-Agent40"
                    const dashIndex = part.lastIndexOf('-')
                    const start = part.substring(0, dashIndex).trim()
                    const end = part.substring(dashIndex + 1).trim()

                    const startNum = parseInt(start)
                    const endNum = parseInt(end)

                    if (!isNaN(startNum) && !isNaN(endNum) && String(startNum) === start && String(endNum) === end) {
                        // Pure numeric range
                        if (startNum > endNum) throw new Error('Invalid range')
                        for (let i = startNum; i <= endNum; i++) {
                            sips.push(String(i))
                        }
                    } else {
                        // Alphanumeric range: Agent1-Agent40
                        const startMatch = start.match(/^(.+?)(\d+)$/)
                        const endMatch = end.match(/^(.+?)(\d+)$/)

                        if (startMatch && endMatch && startMatch[1] === endMatch[1]) {
                            const prefix = startMatch[1]
                            const startN = parseInt(startMatch[2])
                            const endN = parseInt(endMatch[2])

                            if (startN > endN) throw new Error('Invalid range')
                            for (let i = startN; i <= endN; i++) {
                                sips.push(prefix + i)
                            }
                        } else {
                            // Treat as single value (hyphenated name)
                            sips.push(part)
                        }
                    }
                } else {
                    // Single value
                    sips.push(part)
                }
            }

            const uniqueSips = [...new Set(sips)]
            // Smart sort
            uniqueSips.sort((a, b) => {
                const aNum = parseInt(a)
                const bNum = parseInt(b)
                const aIsNum = !isNaN(aNum) && String(aNum) === a
                const bIsNum = !isNaN(bNum) && String(bNum) === b

                if (aIsNum && bIsNum) return aNum - bNum
                if (aIsNum) return -1
                if (bIsNum) return 1
                return a.localeCompare(b, undefined, { numeric: true })
            })

            setSipPreview({ valid: true, count: uniqueSips.length, sips: uniqueSips })
        } catch (e) {
            setSipPreview({ valid: false, count: 0, sips: [], error: e.message })
        }
    }

    const openAddModal = () => {
        setFormData({
            name: '',
            description: '',
            sip_pool: '',
            password: '',
            password_same_as_sip: false,
            dialer_id: '',
            avatar_id: '',
            access_link: '',
            campaign: '',
            campaign_name: '',
            // Bot configuration defaults
            login_method: 'double-merged',
            transfer_method: 'd1',
            xfer_disposition_selector: 'call-transfered',
            transcriber_type: 'VOSK',
            dont_wait: true,
            comment_login_submit_btn: true,
            comment_2nd_hello_logic: true,
            cmnt_40sec_call_limit_check: false,
            dispo_in_cb_after_40sec: false,
            debug_mode: false,
            number_of_audios: 1,
            save_logs_for_sips: '',
            voice_set_pool: '',
            voice_set_ids: []
        })
        setShowAdvanced(false)
        setModalMode('add')
        setCurrentTemplate(null)
        setShowModal(true)
    }

    const openEditModal = (template) => {
        // Parse voice_set_pool to voice_set_ids array
        let voiceSetIds = []
        if (template.voice_set_pool) {
            try {
                const pool = typeof template.voice_set_pool === 'string'
                    ? (template.voice_set_pool.startsWith('[') ? JSON.parse(template.voice_set_pool) : template.voice_set_pool.split(',').map(s => s.trim()))
                    : template.voice_set_pool
                // Convert names to IDs if needed
                voiceSetIds = pool.map(item => {
                    if (typeof item === 'number') return item
                    const vs = voiceSets.find(v => v.name === item || v.id === parseInt(item))
                    return vs ? vs.id : null
                }).filter(id => id !== null)
            } catch (e) {
                console.error('Failed to parse voice_set_pool:', e)
            }
        }

        setFormData({
            name: template.name,
            description: template.description || '',
            sip_pool: template.sip_pool,
            password: template.password || '',
            password_same_as_sip: template.password === '__SAME_AS_SIP__',
            dialer_id: String(template.dialer_id),
            avatar_id: String(template.avatar_id),
            access_link: template.access_link,
            campaign: template.campaign || '',
            campaign_name: template.campaign_name || '',
            // Bot configuration from template
            login_method: template.login_method || 'double-merged',
            transfer_method: template.transfer_method || 'd1',
            xfer_disposition_selector: template.xfer_disposition_selector || 'call-transfered',
            transcriber_type: template.transcriber_type || 'VOSK',
            dont_wait: template.dont_wait !== 0,
            comment_login_submit_btn: template.comment_login_submit_btn !== 0,
            comment_2nd_hello_logic: template.comment_2nd_hello_logic !== 0,
            cmnt_40sec_call_limit_check: template.cmnt_40sec_call_limit_check === 1,
            dispo_in_cb_after_40sec: template.dispo_in_cb_after_40sec === 1,
            debug_mode: template.debug_mode === 1,
            number_of_audios: template.number_of_audios || 1,
            save_logs_for_sips: template.save_logs_for_sips || '',
            voice_set_pool: template.voice_set_pool || '',
            voice_set_ids: voiceSetIds
        })
        setShowAdvanced(false)
        setModalMode('edit')
        setCurrentTemplate(template)
        setShowModal(true)
    }

    // Clone template - opens modal with template data but in "add" mode with modified name
    const cloneTemplate = (template) => {
        // Parse voice_set_pool to voice_set_ids array
        let voiceSetIds = []
        if (template.voice_set_pool) {
            try {
                const pool = typeof template.voice_set_pool === 'string'
                    ? (template.voice_set_pool.startsWith('[') ? JSON.parse(template.voice_set_pool) : template.voice_set_pool.split(',').map(s => s.trim()))
                    : template.voice_set_pool
                voiceSetIds = pool.map(item => {
                    if (typeof item === 'number') return item
                    const vs = voiceSets.find(v => v.name === item || v.id === parseInt(item))
                    return vs ? vs.id : null
                }).filter(id => id !== null)
            } catch (e) {
                console.error('Failed to parse voice_set_pool:', e)
            }
        }

        setFormData({
            name: `${template.name} (Copy)`,
            description: template.description || '',
            sip_pool: '', // Don't copy SIP pool - new template needs its own
            password: template.password || '',
            password_same_as_sip: template.password === '__SAME_AS_SIP__',
            dialer_id: String(template.dialer_id),
            avatar_id: String(template.avatar_id),
            access_link: template.access_link,
            campaign: template.campaign || '',
            campaign_name: template.campaign_name || '',
            // Bot configuration from template
            login_method: template.login_method || 'double-merged',
            transfer_method: template.transfer_method || 'd1',
            xfer_disposition_selector: template.xfer_disposition_selector || 'call-transfered',
            transcriber_type: template.transcriber_type || 'VOSK',
            dont_wait: template.dont_wait !== 0,
            comment_login_submit_btn: template.comment_login_submit_btn !== 0,
            comment_2nd_hello_logic: template.comment_2nd_hello_logic !== 0,
            cmnt_40sec_call_limit_check: template.cmnt_40sec_call_limit_check === 1,
            dispo_in_cb_after_40sec: template.dispo_in_cb_after_40sec === 1,
            debug_mode: template.debug_mode === 1,
            number_of_audios: template.number_of_audios || 1,
            save_logs_for_sips: template.save_logs_for_sips || '',
            voice_set_pool: template.voice_set_pool || '',
            voice_set_ids: voiceSetIds
        })
        setShowAdvanced(false)
        setModalMode('add') // Use 'add' mode so it creates a new template
        setCurrentTemplate(null) // No current template - this will be a new one
        setShowModal(true)
    }

    const closeModal = () => {
        setShowModal(false)
        setCurrentTemplate(null)
    }

    const handleSave = async () => {
        // Password validation: either password_same_as_sip is checked OR password is filled
        const passwordValid = formData.password_same_as_sip || formData.password

        if (!formData.name || !formData.sip_pool || !passwordValid ||
            !formData.dialer_id || !formData.avatar_id || !formData.access_link ||
            !formData.campaign_name) {
            alert('Please fill in all required fields')
            return
        }

        if (!sipPreview.valid || sipPreview.count === 0) {
            alert('Invalid SIP pool format')
            return
        }

        setSaving(true)
        try {
            // Convert voice_set_ids to voice_set_pool format for storage
            const voiceSetPool = formData.voice_set_ids.length > 0
                ? JSON.stringify(formData.voice_set_ids)
                : formData.voice_set_pool || ''

            const data = {
                ...formData,
                // If password_same_as_sip is checked, store a marker; otherwise use the actual password
                password: formData.password_same_as_sip ? '__SAME_AS_SIP__' : formData.password,
                dialer_id: parseInt(formData.dialer_id),
                avatar_id: parseInt(formData.avatar_id),
                voice_set_pool: voiceSetPool
            }
            // Remove frontend-only fields
            delete data.password_same_as_sip
            delete data.voice_set_ids

            if (modalMode === 'edit' && currentTemplate) {
                await templateApi.update(currentTemplate.id, data)
            } else {
                await templateApi.create(data)
            }

            closeModal()
            loadData()
        } catch (error) {
            console.error('Failed to save template:', error)
            alert('Error saving template: ' + error.message)
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!templateToDelete) return

        try {
            await templateApi.delete(templateToDelete.id)
            setShowDeleteModal(false)
            setTemplateToDelete(null)
            loadData()
        } catch (error) {
            console.error('Failed to delete template:', error)
            alert('Error deleting template: ' + (error.response?.data?.error || error.message))
        }
    }

    const filteredTemplates = templates.filter(t =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(searchQuery.toLowerCase())
    )

    if (loading) {
        return (
            <div className="templates-page">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                </div>
            </div>
        )
    }

    return (
        <div className="templates-page">
            {/* Header */}
            <div className="page-header">
                <div className="page-header-left">
                    <h1 className="page-title">Templates</h1>
                    <p className="page-subtitle">
                        Create reusable configurations for bulk machine activation
                    </p>
                </div>
                <div className="page-header-actions">
                    <button
                        onClick={refresh}
                        className="btn btn-secondary btn-icon"
                        disabled={refreshing}
                    >
                        <RefreshCw size={18} className={refreshing ? 'spinner' : ''} />
                    </button>
                    <button onClick={openAddModal} className="btn btn-primary">
                        <Plus size={18} />
                        New Template
                    </button>
                </div>
            </div>

            {/* Stats Bar */}
            <div className="stat-blocks">
                <div className="stat-block blue">
                    <div className="stat-block-icon"><Layers size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{templates.length}</span>
                        <span className="stat-block-label">Templates</span>
                    </div>
                </div>
                <div className="stat-block green">
                    <div className="stat-block-icon"><Server size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{templates.reduce((sum, t) => sum + (t.sip_count || 0), 0)}</span>
                        <span className="stat-block-label">Total SIPs</span>
                    </div>
                </div>
                <div className="stat-block purple">
                    <div className="stat-block-icon"><Cpu size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{templates.reduce((sum, t) => sum + (t.active_machines || 0), 0)}</span>
                        <span className="stat-block-label">Active Machines</span>
                    </div>
                </div>
                <div className="stat-block orange">
                    <div className="stat-block-icon"><FileText size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{templates.filter(t => t.avatar_id).length}</span>
                        <span className="stat-block-label">With Avatar</span>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="toolbar">
                <div className="search-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                </div>
            </div>

            {/* Templates Grid */}
            {filteredTemplates.length > 0 ? (
                <div className="templates-grid">
                    {filteredTemplates.map(template => (
                        <motion.div
                            key={template.id}
                            className="template-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="template-card-header">
                                <div>
                                    <h3 className="template-card-title">{template.name}</h3>
                                    {template.description && (
                                        <p className="template-card-description">{template.description}</p>
                                    )}
                                </div>
                                <div className="template-card-actions">
                                    <button
                                        onClick={() => openEditModal(template)}
                                        className="btn btn-ghost btn-icon"
                                        title="Edit"
                                    >
                                        <Edit size={16} />
                                    </button>
                                    <button
                                        onClick={() => cloneTemplate(template)}
                                        className="btn btn-ghost btn-icon"
                                        title="Clone Template"
                                        style={{ color: '#8b5cf6' }}
                                    >
                                        <Copy size={16} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            setTemplateToDelete(template)
                                            setShowDeleteModal(true)
                                        }}
                                        className="btn btn-ghost btn-icon"
                                        title="Delete"
                                        style={{ color: '#ef4444' }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="template-card-body">
                                <div className="template-stat">
                                    <Server size={16} className="template-stat-icon" />
                                    <span className="template-stat-value">{template.sip_count || 0}</span>
                                    <span>SIPs in pool</span>
                                </div>
                                <div className="template-stat">
                                    <Users size={16} className="template-stat-icon" />
                                    <span className="template-stat-value">{template.active_machines || 0}</span>
                                    <span>active machines</span>
                                </div>
                                <div className="template-stat">
                                    <FileText size={16} className="template-stat-icon" />
                                    <span>{template.avatar_name || 'No avatar'}</span>
                                </div>
                            </div>

                            <div className="template-card-footer">
                                <span className="template-tag">{template.dialer_name || 'No dialer'}</span>
                                {template.campaign_name && <span className="template-tag">{template.campaign_name}</span>}
                            </div>
                        </motion.div>
                    ))}
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <FileText size={32} />
                    </div>
                    <h3 className="empty-state-title">No templates found</h3>
                    <p className="empty-state-text">Create your first template to get started</p>
                    <button onClick={openAddModal} className="btn btn-primary" style={{ marginTop: '16px' }}>
                        <Plus size={18} />
                        Create First Template
                    </button>
                </div>
            )}

            {/* Add/Edit Modal */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModal}
                    >
                        <motion.div
                            className="modal"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h2 className="modal-title">
                                    {modalMode === 'edit' ? 'Edit Template' : 'New Template'}
                                </h2>
                                <button onClick={closeModal} className="modal-close">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">
                                        Template Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., Solar Campaign A"
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Optional description..."
                                        className="form-input form-textarea"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        SIP Pool <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.sip_pool}
                                        onChange={(e) => setFormData({ ...formData, sip_pool: e.target.value })}
                                        placeholder="e.g., 8001-8005, 8007, 8010-8050"
                                        className="form-input"
                                    />
                                    <div className={`sip-preview ${sipPreview.valid ? 'valid' : formData.sip_pool ? 'invalid' : 'neutral'}`}>
                                        {sipPreview.valid ? (
                                            <>✓ {sipPreview.count} SIPs parsed: {sipPreview.sips.slice(0, 5).join(', ')}
                                                {sipPreview.count > 5 && ` ... and ${sipPreview.count - 5} more`}</>
                                        ) : formData.sip_pool ? (
                                            <>✗ Invalid format</>
                                        ) : (
                                            <>Supports: 8001-8005, Agent1-Agent40, Bot1-Bot20</>
                                        )}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Password <span className="required">*</span>
                                    </label>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '8px',
                                        fontSize: '0.85rem',
                                        cursor: 'pointer'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.password_same_as_sip}
                                            onChange={(e) => setFormData({ ...formData, password_same_as_sip: e.target.checked, password: '' })}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <span style={{ color: 'var(--text-secondary)' }}>Password is same as SIP ID</span>
                                    </label>
                                    {!formData.password_same_as_sip && (
                                        <input
                                            type="text"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            placeholder="Default password for all licenses"
                                            className="form-input"
                                        />
                                    )}
                                    {formData.password_same_as_sip && (
                                        <div style={{
                                            padding: '10px 12px',
                                            background: 'var(--bg-secondary)',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            color: 'var(--text-muted)'
                                        }}>
                                            Each license will use its SIP ID as password (e.g., 8001 → 8001, Agent1 → Agent1)
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Dialer <span className="required">*</span>
                                    </label>
                                    <SearchableDropdown
                                        options={dialers}
                                        value={formData.dialer_id}
                                        onChange={(val) => setFormData({ ...formData, dialer_id: val })}
                                        placeholder="Select Dialer..."
                                        labelKey="name"
                                        valueKey="id"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Avatar <span className="required">*</span>
                                    </label>
                                    <SearchableDropdown
                                        options={avatars}
                                        value={formData.avatar_id}
                                        onChange={(val) => setFormData({ ...formData, avatar_id: val })}
                                        placeholder="Select Avatar..."
                                        labelKey="name"
                                        valueKey="id"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Access Link <span className="required">*</span>
                                    </label>
                                    <SearchableDropdown
                                        options={accessLinks.map(link => ({ id: link, name: link }))}
                                        value={formData.access_link}
                                        onChange={(val) => setFormData({ ...formData, access_link: val })}
                                        placeholder="Select Access Link..."
                                        labelKey="name"
                                        valueKey="id"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Campaign Name <span className="required">*</span>
                                    </label>
                                    <SearchableDropdown
                                        options={[
                                            { id: 'Medicare', name: 'Medicare' },
                                            { id: 'Final Expense', name: 'Final Expense' },
                                            { id: 'Home Improvement', name: 'Home Improvement' },
                                            { id: 'Medicaid', name: 'Medicaid' },
                                            { id: 'Real Estate', name: 'Real Estate' },
                                            { id: 'Diabetes', name: 'Diabetes' },
                                            { id: 'Auto', name: 'Auto' },
                                            { id: 'MVA', name: 'MVA' },
                                            { id: 'Med Alert', name: 'Med Alert' },
                                            { id: 'ACA', name: 'ACA' },
                                            { id: 'Solar', name: 'Solar' },
                                            { id: 'MedBots', name: 'MedBots' },
                                            { id: 'DNC', name: 'DNC' }
                                        ]}
                                        value={formData.campaign_name}
                                        onChange={(val) => setFormData({ ...formData, campaign_name: val })}
                                        placeholder="Select Campaign..."
                                        labelKey="name"
                                        valueKey="id"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Campaign ID</label>
                                    <input
                                        type="text"
                                        value={formData.campaign}
                                        onChange={(e) => setFormData({ ...formData, campaign: e.target.value })}
                                        placeholder="Optional campaign ID for bot"
                                        className="form-input"
                                    />
                                </div>

                                {/* Bot Configuration Toggle */}
                                <div className="form-divider">
                                    <button
                                        type="button"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        className="btn btn-ghost"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        {showAdvanced ? '▼ Hide Bot Configuration' : '▶ Show Bot Configuration'}
                                    </button>
                                </div>

                                {showAdvanced && (
                                    <div className="bot-config-section">
                                        <h4 style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>Bot Configuration</h4>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">Login Method</label>
                                                <SearchableDropdown
                                                    options={[
                                                        { id: 'single-v1', name: 'Single V1' },
                                                        { id: 'single-v2', name: 'Single V2' },
                                                        { id: 'repeating', name: 'Repeating' },
                                                        { id: 'double', name: 'Double' },
                                                        { id: 'double-merged', name: 'Double Merged' },
                                                        { id: 'second-try', name: 'Second Try' },
                                                        { id: 'at-law', name: 'At Law' },
                                                        { id: 'double-ziscom', name: 'Double Ziscom' }
                                                    ]}
                                                    value={formData.login_method}
                                                    onChange={(val) => setFormData({ ...formData, login_method: val })}
                                                    placeholder="Select Login Method..."
                                                    labelKey="name"
                                                    valueKey="id"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">Transfer Method</label>
                                                <SearchableDropdown
                                                    options={[
                                                        { id: 'dynamic', name: 'Dynamic' },
                                                        { id: 'inboundlocal', name: 'Inbound Local' },
                                                        { id: 'd1', name: 'D1' },
                                                        { id: 'd2', name: 'D2' },
                                                        { id: 'd3', name: 'D3' },
                                                        { id: 'd4', name: 'D4' },
                                                        { id: 'd5', name: 'D5' },
                                                        { id: 'd2-preset', name: 'D2 Preset' },
                                                        { id: 'Quick', name: 'Quick' },
                                                        { id: 'local', name: 'Local' },
                                                        { id: 'local-group', name: 'Local Group' },
                                                        { id: 'preset-dial-with-customer', name: 'Preset Dial With Customer' },
                                                        { id: 'dial-with-customer', name: 'Dial With Customer' },
                                                        { id: 'd1-preset', name: 'D1 Preset' },
                                                        { id: 'd4-preset', name: 'D4 Preset' },
                                                        { id: 'preset-voice', name: 'Preset Voice' },
                                                        { id: 'd1-cons-dwc', name: 'D1 Cons DWC' },
                                                        { id: 'd1-dial-with-customer', name: 'D1 Dial With Customer' },
                                                        { id: 'd2-dial-with-customer', name: 'D2 Dial With Customer' },
                                                        { id: 'localcloserconsultative', name: 'Local Closer Consultative' }
                                                    ]}
                                                    value={formData.transfer_method}
                                                    onChange={(val) => setFormData({ ...formData, transfer_method: val })}
                                                    placeholder="Select Transfer Method..."
                                                    labelKey="name"
                                                    valueKey="id"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">Transfer Disposition</label>
                                                <SearchableDropdown
                                                    options={[
                                                        { id: 'call-transfered', name: 'Call Transfered' },
                                                        { id: 'sale-made', name: 'Sale Made' }
                                                    ]}
                                                    value={formData.xfer_disposition_selector}
                                                    onChange={(val) => setFormData({ ...formData, xfer_disposition_selector: val })}
                                                    placeholder="Select Disposition..."
                                                    labelKey="name"
                                                    valueKey="id"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">Transcriber</label>
                                                <SearchableDropdown
                                                    options={[
                                                        { id: 'VOSK', name: 'VOSK (Small)' },
                                                        { id: 'VOSK_V15L', name: 'VOSK (V15 Large)' },
                                                        { id: 'HYBRID', name: 'HYBRID (VOSK + Whisper)' },
                                                        { id: 'WHISPER_CPU', name: 'Whisper CPU' }
                                                    ]}
                                                    value={formData.transcriber_type}
                                                    onChange={(val) => setFormData({ ...formData, transcriber_type: val })}
                                                    placeholder="Select Transcriber..."
                                                    labelKey="name"
                                                    valueKey="id"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">Number of Audios</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="10"
                                                    value={formData.number_of_audios}
                                                    onChange={(e) => setFormData({ ...formData, number_of_audios: parseInt(e.target.value) || 1 })}
                                                    className="form-input"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">Save Logs for SIPs</label>
                                                <input
                                                    type="text"
                                                    value={formData.save_logs_for_sips}
                                                    onChange={(e) => setFormData({ ...formData, save_logs_for_sips: e.target.value })}
                                                    placeholder='["1081", "1082"]'
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Voice Set Pool</label>
                                            <div className="voice-set-quick-select">
                                                <button type="button" className="quick-btn" onClick={() => setFormData({ ...formData, voice_set_ids: voiceSets.map(vs => vs.id) })}>All</button>
                                                <button type="button" className="quick-btn male" onClick={() => setFormData({ ...formData, voice_set_ids: voiceSets.filter(vs => vs.gender?.toLowerCase() === 'male').map(vs => vs.id) })}>Male</button>
                                                <button type="button" className="quick-btn female" onClick={() => setFormData({ ...formData, voice_set_ids: voiceSets.filter(vs => vs.gender?.toLowerCase() === 'female').map(vs => vs.id) })}>Female</button>
                                                <button type="button" className="quick-btn clear" onClick={() => setFormData({ ...formData, voice_set_ids: [] })}>Clear</button>
                                            </div>
                                            <div className="voice-set-grid">
                                                {voiceSets.map(vs => (
                                                    <div
                                                        key={vs.id}
                                                        className={`voice-set-item ${formData.voice_set_ids.includes(vs.id) ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            const ids = formData.voice_set_ids.includes(vs.id)
                                                                ? formData.voice_set_ids.filter(id => id !== vs.id)
                                                                : [...formData.voice_set_ids, vs.id]
                                                            setFormData({ ...formData, voice_set_ids: ids })
                                                        }}
                                                    >
                                                        <div className="vs-info">
                                                            <span className="vs-name">{vs.name}</span>
                                                            {vs.gender && <span className={`vs-gender ${vs.gender.toLowerCase()}`}>{vs.gender}</span>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="voice-set-count">{formData.voice_set_ids.length} voice set(s) selected</div>
                                        </div>

                                        <div className="checkbox-group">
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.dont_wait}
                                                    onChange={(e) => setFormData({ ...formData, dont_wait: e.target.checked })}
                                                />
                                                <span>Don't Wait for Timeouts</span>
                                            </label>

                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.comment_login_submit_btn}
                                                    onChange={(e) => setFormData({ ...formData, comment_login_submit_btn: e.target.checked })}
                                                />
                                                <span>Skip Login Button</span>
                                            </label>

                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.comment_2nd_hello_logic}
                                                    onChange={(e) => setFormData({ ...formData, comment_2nd_hello_logic: e.target.checked })}
                                                />
                                                <span>Skip 2nd Hello</span>
                                            </label>

                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.cmnt_40sec_call_limit_check}
                                                    onChange={(e) => setFormData({ ...formData, cmnt_40sec_call_limit_check: e.target.checked })}
                                                />
                                                <span>Skip 40s Call Limit</span>
                                            </label>

                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.dispo_in_cb_after_40sec}
                                                    onChange={(e) => setFormData({ ...formData, dispo_in_cb_after_40sec: e.target.checked })}
                                                />
                                                <span>Dispo CB After 40s</span>
                                            </label>

                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.debug_mode}
                                                    onChange={(e) => setFormData({ ...formData, debug_mode: e.target.checked })}
                                                />
                                                <span>Debug Mode</span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button onClick={closeModal} className="btn btn-secondary">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="btn btn-primary"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 size={16} className="spinner" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={16} />
                                            {modalMode === 'edit' ? 'Save Changes' : 'Create Template'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowDeleteModal(false)}
                    >
                        <motion.div
                            className="modal delete-modal"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-body">
                                <div className="delete-modal-icon">
                                    <Trash2 size={24} />
                                </div>
                                <h3 className="delete-modal-title">Delete Template?</h3>
                                <p className="delete-modal-text">
                                    Are you sure you want to delete "{templateToDelete?.name}"?
                                    This cannot be undone.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="btn btn-danger"
                                >
                                    <Trash2 size={16} />
                                    Delete
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default Templates